import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Lock,
  Phone,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Shield,
  Zap,
  Users,
  Eye,
  EyeOff,
} from 'lucide-react';
import useAuth from '../hooks/useAuth';
import api from '../api/api';
import styles from './LoginPage.module.css';
import logo from '../assets/images/zone_icon.png';

// ─────────────────────────────────────────────────────────────
// FEATURES — Panneau gauche
// ─────────────────────────────────────────────────────────────
const FEATURES = [
  {
    Icon: Zap,
    title: 'IA Générative',
    desc: 'Fiches de poste & dossiers en quelques secondes',
  },
  {
    Icon: Shield,
    title: 'Données sécurisées',
    desc: 'Vos données restent confidentielles et protégées',
  },
  {
    Icon: Users,
    title: 'Gestion RH centralisée',
    desc: 'Pilotez vos talents depuis un seul endroit',
  },
];

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const LoginPage = () => {
  // Champs
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone]               = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [forgotEmail, setForgotEmail]   = useState('');

  // UI
  const [view, setView]           = useState('login'); // 'login' | 'contact' | 'forgot'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [resetSent, setResetSent]   = useState(false);

  // Auth
  const [tempToken, setTempToken] = useState(null);
  const { login }  = useAuth();
  const navigate   = useNavigate();

  // ── Helpers ──────────────────────────────────────────────
  const clearMessages = () => { setError(''); setSuccessMsg(''); };

  const goTo = (v) => { clearMessages(); setView(v); };

  // ── Handlers ─────────────────────────────────────────────
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      setTempToken(data.access_token);
      setView('contact');
    } catch {
      setError('Email ou mot de passe invalide.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!phone || !contactEmail) { setError('Tous les champs sont requis.'); return; }
    setIsLoading(true);
    setTimeout(() => {
      login(tempToken, contactEmail, phone);
      navigate('/home');
    }, 900);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      await api.post('/auth/request-password-reset', { email: forgotEmail });
      setResetSent(true);
      setSuccessMsg(
        'Si un compte existe avec cet email, un lien de réinitialisation a été envoyé.'
      );
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>

      {/* ── Orbes décoratifs ── */}
      <div className={styles.orb1} aria-hidden="true" />
      <div className={styles.orb2} aria-hidden="true" />
      <div className={styles.orb3} aria-hidden="true" />

      {/* ══ CARTE PRINCIPALE ══ */}
      <div className={styles.card}>

        {/* ── Panneau gauche — Branding ── */}
        <aside className={styles.leftPanel} aria-hidden="true">
          <div className={styles.leftInner}>

            {/* Logo */}
            <div className={styles.logoWrap}>
              <div className={styles.logoGlow} />
              <img src={logo} alt="CVtoSkill" className={styles.logo} />
            </div>

            {/* Textes */}
            <div className={styles.brandText}>
              <h1 className={styles.brandTitle}>
                Bienvenue sur{' '}
                <span className={styles.brandHighlight}>CVtoSkill</span>
              </h1>
              <p className={styles.brandSub}>
                La plateforme RH intelligente qui valorise vos talents.
              </p>
            </div>

            {/* Features */}
            <ul className={styles.featureList} role="list">
              {FEATURES.map(({ Icon, title, desc }) => (
                <li key={title} className={styles.featureItem}>
                  <span className={styles.featureIconWrap}>
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className={styles.featureText}>
                    <strong>{title}</strong>
                    <small>{desc}</small>
                  </span>
                </li>
              ))}
            </ul>

            {/* Badge */}
            <div className={styles.aiBadge}>
              <Sparkles size={13} strokeWidth={2.5} />
              Propulsé par l'IA
            </div>
          </div>
        </aside>

        {/* ── Panneau droit — Formulaires ── */}
        <main className={styles.rightPanel}>
          <div className={styles.formBox}>

            {/* ══ VUE : LOGIN ══ */}
            {view === 'login' && (
              <section className={styles.formSection} key="login">

                <FormHeader
                  title="Connexion"
                  subtitle="Accédez à votre espace personnel"
                />

                {error && <Alert type="error" message={error} />}

                <form onSubmit={handleLoginSubmit} className={styles.form} noValidate>

                  <Field
                    id="email"
                    type="email"
                    label="Email professionnel"
                    value={email}
                    onChange={setEmail}
                    Icon={Mail}
                    required
                    autoComplete="email"
                  />

                  <Field
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Mot de passe"
                    value={password}
                    onChange={setPassword}
                    Icon={Lock}
                    required
                    autoComplete="current-password"
                    suffix={
                      <button
                        type="button"
                        className={styles.eyeBtn}
                        onClick={() => setShowPassword((p) => !p)}
                        aria-label={showPassword ? 'Masquer' : 'Afficher'}
                      >
                        {showPassword
                          ? <EyeOff size={16} strokeWidth={1.75} />
                          : <Eye    size={16} strokeWidth={1.75} />
                        }
                      </button>
                    }
                  />

                  <SubmitButton loading={isLoading} label="Se connecter" />
                </form>

                <div className={styles.textLink}>
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => goTo('forgot')}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>

                <Divider />

                <p className={styles.signupHint}>
                  Nouveau sur CVtoSkill ?{' '}
                  <a href="/demo" className={styles.linkInline}>
                    Créer un compte
                  </a>
                </p>
              </section>
            )}

            {/* ══ VUE : CONTACT ══ */}
            {view === 'contact' && (
              <section className={styles.formSection} key="contact">

                <StepBadge current={2} total={2} />

                <FormHeader
                  title="Dernière étape"
                  subtitle="Vérifiez vos informations de contact"
                />

                {error && <Alert type="error" message={error} />}

                <form onSubmit={handleContactSubmit} className={styles.form} noValidate>

                  <Field
                    id="contactEmail"
                    type="email"
                    label="Email de contact"
                    value={contactEmail}
                    onChange={setContactEmail}
                    Icon={Mail}
                    required
                    autoComplete="email"
                  />

                  <Field
                    id="phone"
                    type="tel"
                    label="Téléphone"
                    value={phone}
                    onChange={setPhone}
                    Icon={Phone}
                    required
                    autoComplete="tel"
                  />

                  <SubmitButton loading={isLoading} label="Accéder à mon espace" />
                </form>
              </section>
            )}

            {/* ══ VUE : MOT DE PASSE OUBLIÉ ══ */}
            {view === 'forgot' && (
              <section className={styles.formSection} key="forgot">

                <FormHeader
                  title="Réinitialisation"
                  subtitle="Recevez un lien par email pour changer votre mot de passe"
                />

                {error      && <Alert type="error"   message={error}      />}
                {successMsg && <Alert type="success" message={successMsg} />}

                {!resetSent ? (
                  <form onSubmit={handleForgotSubmit} className={styles.form} noValidate>

                    <Field
                      id="forgotEmail"
                      type="email"
                      label="Votre adresse email"
                      value={forgotEmail}
                      onChange={setForgotEmail}
                      Icon={Mail}
                      required
                      autoComplete="email"
                    />

                    <SubmitButton loading={isLoading} label="Envoyer le lien" />
                  </form>
                ) : (
                  <div className={styles.resetSuccess}>
                    <CheckCircle2
                      size={48}
                      strokeWidth={1.5}
                      className={styles.resetIcon}
                    />
                    <p>Vérifiez votre boîte de réception.</p>
                  </div>
                )}

                <div className={styles.textLink}>
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => {
                      goTo('login');
                      setResetSent(false);
                    }}
                  >
                    <ArrowLeft size={14} strokeWidth={2} />
                    Retour à la connexion
                  </button>
                </div>
              </section>
            )}

          </div>
        </main>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SOUS-COMPOSANTS
// ─────────────────────────────────────────────────────────────

/** En-tête de formulaire */
const FormHeader = ({ title, subtitle }) => (
  <div className={styles.formHeader}>
    <h2 className={styles.formTitle}>{title}</h2>
    <p className={styles.formSub}>{subtitle}</p>
  </div>
);

/** Champ de formulaire avec label flottant */
const Field = ({
  id, type, label, value, onChange,
  Icon, required, autoComplete, suffix,
}) => (
  <div className={styles.field}>
    <div className={styles.inputWrap}>
      <span className={styles.inputIcon}>
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <input
        id={id}
        type={type}
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder=" "
        required={required}
        autoComplete={autoComplete}
      />
      <label htmlFor={id} className={styles.label}>{label}</label>
      {suffix && <div className={styles.inputSuffix}>{suffix}</div>}
    </div>
  </div>
);

/** Bouton de soumission */
const SubmitButton = ({ loading, label }) => (
  <button
    type="submit"
    className={`${styles.submitBtn} ${loading ? styles.submitBtnLoading : ''}`}
    disabled={loading}
  >
    {loading ? (
      <>
        <Loader2 size={18} strokeWidth={2} className={styles.spinner} />
        <span>Chargement…</span>
      </>
    ) : (
      <>
        <span>{label}</span>
        <ArrowRight size={16} strokeWidth={2.5} />
      </>
    )}
  </button>
);

/** Alerte d'erreur ou de succès */
const Alert = ({ type, message }) => {
  const Icon = type === 'error' ? AlertCircle : CheckCircle2;
  return (
    <div className={`${styles.alert} ${styles[`alert--${type}`]}`} role="alert">
      <Icon size={16} strokeWidth={2} className={styles.alertIcon} />
      <span>{message}</span>
    </div>
  );
};

/** Séparateur */
const Divider = () => (
  <div className={styles.divider}>
    <span>ou</span>
  </div>
);

/** Indicateur d'étape */
const StepBadge = ({ current, total }) => (
  <div className={styles.stepBadge}>
    Étape {current} sur {total}
  </div>
);

export default LoginPage;